import { AlertTriangle, ArrowRight, CheckCircle2, ShieldAlert, ShieldCheck, ShieldX, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { BackupHealth, HealthAction } from '@/modules/backups/health';
import { divider, hairline, panel, toneText, urgency } from '@/components/surface/tones';

/**
 * Backup health: one word, and the reasons for it beside it.
 *
 * ## Urgency before reading
 *
 * When something is wrong the panel takes the state's colour at its edge and in the status block,
 * so "At risk" registers before a word of it is read. The reasons are listed worst first; a problem
 * is set in the heavier weight, a warning in the regular one, and each has its fix aligned in one
 * column on the right, so the eye can run straight down to the action.
 *
 * ## The reasons are the point
 *
 * "At risk" on its own is an alarm with no instructions; next to it, "shop-api failed its latest
 * restore test · View tests" is something a person can act on in one click. When nothing is wrong
 * the same space says what "healthy" rests on, so the green word is never a claim without its
 * evidence.
 *
 * Links that open a dialog wait for an active subscription, as the buttons they stand in for do.
 */
export function HealthStrip({
  health,
  permitsChanges,
  onAction,
}: {
  health: BackupHealth;
  permitsChanges: boolean;
  onAction: (action: HealthAction) => void;
}) {
  const Shield = health.status === 'healthy' ? ShieldCheck : health.status === 'risk' ? ShieldX : ShieldAlert;
  const style = urgency[health.status];

  const guidance =
    health.status === 'risk' ? 'Action needed' : health.status === 'attention' ? 'Review recommended' : 'Nothing needs your attention';

  return (
    <section
      aria-label="Backup health"
      className={cn(panel, 'grid overflow-hidden md:grid-cols-[15rem_minmax(0,1fr)]', style.ring)}
    >
      <div
        className={cn(
          'flex items-center gap-3.5 px-5 py-4 md:flex-col md:items-start md:justify-center md:gap-3 md:border-r md:py-5',
          hairline,
          style.block,
        )}
      >
        <span className={cn('inline-flex size-11 shrink-0 items-center justify-center rounded-full', style.icon)}>
          <Shield className="size-[22px]" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Backup health</p>
          <p className={cn('text-xl font-semibold leading-7 tracking-tight', toneText[health.tone])}>{health.label}</p>
          <p className="text-[13px] text-muted-foreground">{guidance}</p>
        </div>
      </div>

      {health.status === 'healthy' ? (
        <ul className="grid content-center gap-x-8 gap-y-2.5 px-5 py-4 sm:grid-cols-2">
          {health.reasons.map((reason) => (
            <li key={reason.key} className="flex items-start gap-2.5 text-sm text-foreground">
              <CheckCircle2 className="mt-px size-[18px] shrink-0 text-success" aria-hidden />
              <span className="min-w-0">{reason.text}</span>
            </li>
          ))}
        </ul>
      ) : (
        <ul className={cn('divide-y px-5 py-1.5', divider)}>
          {health.reasons.map((reason) => {
            const danger = reason.severity === 'danger';
            const Icon = danger ? XCircle : AlertTriangle;
            const opensDialog = reason.action !== undefined && reason.action.kind !== 'jump';

            return (
              <li
                key={reason.key}
                className={cn(
                  'grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-1 py-2.5',
                  reason.action !== undefined && 'sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center',
                )}
              >
                <Icon className={cn('mt-px size-[18px] shrink-0 sm:mt-0', danger ? toneText.danger : toneText.warning)} aria-hidden />
                <span className={cn('min-w-0 text-sm', danger ? 'font-medium text-foreground' : 'text-foreground/90')}>
                  {reason.text}
                </span>
                {reason.action !== undefined && (
                  <button
                    type="button"
                    onClick={() => reason.action !== undefined && onAction(reason.action)}
                    disabled={opensDialog && !permitsChanges}
                    className="col-start-2 -ml-2 inline-flex items-center gap-1 justify-self-start whitespace-nowrap rounded-md px-2 py-1 text-[13px] font-medium text-primary transition-colors hover:bg-primary/[0.08] disabled:pointer-events-none disabled:opacity-50 sm:col-start-3 sm:ml-0 sm:justify-self-end"
                  >
                    {reason.action.label}
                    <ArrowRight className="size-3.5" aria-hidden />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
